/**
 * Registers an epic on `epic$` at most once per page session, keyed by a stable string.
 *
 * `epic$` is a page-level singleton (a BehaviorSubject merged via mergeMap — see
 * redux/rootEpics.js) that never tears down a previously-registered epic: calling
 * `epic$.next(epic)` again for what is logically the same epic starts a second,
 * independently-running copy instead of replacing the first. Plugins that get re-instantiated
 * across a bootstrap/teardown cycle (e.g. the mobile modal's open/close) need this guard, or
 * reopening silently duplicates whatever the epic does (double navigateTo dispatch, double
 * fetch, etc.) — see NHS20-8164.
 *
 * @module nativeInteractiveSiteMap/redux/registerEpicOnce
 */

const registeredKeys = new Set();

/**
 * @param {import('rxjs').Subject} epic$
 * @param {string} key - Stable, unique identifier for this epic. Must be a fixed string, not
 *   the epic function itself — factories create a new function reference on every call, so
 *   reference identity can't be used to detect "the same epic" across bootstraps.
 * @param {import('redux-observable').Epic} epic
 */
export default function registerEpicOnce(epic$, key, epic) {
  if (registeredKeys.has(key)) {
    return;
  }
  registeredKeys.add(key);
  epic$.next(epic);
}
